import { streetNumberNameOnly } from './lightstoneAddress';

/**
 * Clean a stored property title for display.
 *
 * Some listings were imported with the full street address (incl. country)
 * dumped into the `title` field - e.g.
 *   "Development at 1 La Camargue Road, Melodie AH, Hartbeespoort, South Africa"
 * Rendering that verbatim everywhere (composer dropdowns, kanban cards, link
 * lists) makes the UI look broken. We never mutate the stored value, but we
 * normalise it at every render site by:
 *   1. Removing a duplicated address suffix (legacy behavior).
 *   2. Stripping a trailing country name.
 *   3. Stripping a trailing 4-5 digit postal code.
 *   4. Stripping trailing tokens that match the listing's own
 *      `locationDetails.country | city | suburb` (when provided).
 *
 * The optional `locationDetails` arg lets callers prune address tail tokens
 * that match the listing's own metadata. Unused-args are tolerated so existing
 * single-arg call sites keep working.
 */

const COUNTRY_TAILS = [
  'South Africa', 'Namibia', 'Botswana', 'Zimbabwe', 'Eswatini', 'Swaziland',
  'Mozambique', 'Lesotho', 'Mauritius', 'Kenya', 'Tanzania', 'Nigeria',
  'United States', 'United States of America', 'USA', 'U.S.A.',
  'United Kingdom', 'UK', 'England', 'Scotland', 'Wales',
  'Australia', 'Canada', 'New Zealand', 'Ireland',
  'Portugal', 'Spain', 'France', 'Italy', 'Germany', 'Netherlands',
  'United Arab Emirates', 'UAE',
];

function escapeRegExp(s) {
  return String(s).replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

// Compile once at module load - these regexes are pure-static and were
// previously rebuilt on every call. With ~30 listings rendered at once
// that's 800+ unnecessary RegExp constructions per render.
const COUNTRY_TAIL_RE = new RegExp(
  `,\\s*(?:${COUNTRY_TAILS.map(escapeRegExp).join('|')})\\s*$`,
  'i',
);
const POSTAL_TAIL_RE = /,\s*\d{4,5}\s*$/;

// Render-time memoisation. Property tables often render the same titles
// dozens of times across mounts; caching the cleaned form is essentially
// free and converts the function into a hashmap lookup after the first
// pass.
const CACHE = new Map();
const CACHE_LIMIT = 2000;

function stripRepeatingSuffix(s) {
  const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return s;
  for (let len = 1; len <= Math.floor(parts.length / 2); len++) {
    const suffix = parts.slice(-len);
    const rest = parts.slice(0, -len);
    for (let i = 0; i + len <= rest.length; i++) {
      if (rest.slice(i, i + len).every((p, j) => p.toLowerCase() === suffix[j].toLowerCase())) {
        return rest.slice(0, i + len).join(', ').trim() || s;
      }
    }
  }
  return s.trim() || s;
}

export function dedupePropertyTitle(title, locationDetails) {
  if (!title || typeof title !== 'string') return title || '';
  const trimmed = title.trim();
  if (!trimmed) return '';

  const cacheKey = locationDetails && typeof locationDetails === 'object'
    ? `${trimmed}|${locationDetails.country || ''}|${locationDetails.city || ''}|${locationDetails.suburb || ''}`
    : trimmed;
  const cached = CACHE.get(cacheKey);
  if (cached !== undefined) return cached;

  let s = stripRepeatingSuffix(trimmed);
  s = s.replace(COUNTRY_TAIL_RE, '').trim();
  s = s.replace(POSTAL_TAIL_RE, '').trim();

  if (locationDetails && typeof locationDetails === 'object') {
    const tail = [locationDetails.country, locationDetails.city, locationDetails.suburb]
      .filter((v) => typeof v === 'string' && v.trim())
      .map((v) => new RegExp(`,\\s*${escapeRegExp(v.trim())}\\s*$`, 'i'));
    let changed = true;
    let safety = 0;
    while (changed && safety++ < 6) {
      changed = false;
      for (const re of tail) {
        if (re.test(s)) {
          s = s.replace(re, '').trim();
          changed = true;
        }
      }
    }
  }

  const out = s.trim() || trimmed;
  if (CACHE.size >= CACHE_LIMIT) CACHE.clear();
  CACHE.set(cacheKey, out);
  return out;
}

/**
 * Same logic as dedupePropertyTitle - exported for callers that semantically
 * want to clean an arbitrary address-shaped string (e.g. description tail).
 */
export function dedupeAddress(str) {
  return dedupePropertyTitle(str || '');
}

/**
 * Extract a listing's public reference / web-ref number from any of the many
 * places it can be stored across imports (PropData, iGrow, ImmoAfrica, manual).
 * Returns a trimmed string, or '' when the listing has no reference.
 */
export function getListingRef(property) {
  if (!property || typeof property !== 'object') return '';
  const ext = property.externalIds || {};
  const meta = property.listingMetadata || {};
  const ref =
    property.reference ||
    property.webRef ||
    property.web_ref ||
    property.importListingRef ||
    property.propdataWebRef ||
    (ext.propdata && ext.propdata.webRef) ||
    ext.igrowWebRef ||
    (ext.immoafrica && ext.immoafrica.reference) ||
    (meta.propdata && meta.propdata.webRef) ||
    meta.reference ||
    // snapshots (e.g. linkedPropertySnapshot) sometimes nest the ref
    (property.linkedPropertySnapshot && property.linkedPropertySnapshot.webRef);
  return ref ? String(ref).trim() : '';
}

/**
 * Street address for listing cards / pipeline labels. Marketing heading and
 * description are ignored — Tyson cards show ref + address only.
 */
export function listingStreetAddress(property) {
  if (!property || typeof property !== 'object') return '';
  const ld = property.locationDetails || (property.details && property.details.locationDetails) || {};
  const streetRaw = typeof ld.streetAddress === 'string' ? ld.streetAddress.trim() : '';
  const street = streetRaw ? streetNumberNameOnly(streetRaw) : '';
  const parts = [street, ld.suburb, ld.city].filter((p) => typeof p === 'string' && p.trim());
  const seen = new Set();
  const uniq = [];
  parts.forEach((p) => {
    const key = p.trim().toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    uniq.push(p.trim());
  });
  if (uniq.length) return uniq.join(', ');
  const loc = property.location || property.address || '';
  return loc ? dedupeAddress(String(loc)) : '';
}

/**
 * Listing-management / pipeline primary line: agency ref + street address.
 * Does not use marketing heading or description.
 */
export function buildListingCardLabel(property) {
  if (!property || typeof property !== 'object') return 'Untitled listing';
  const ref = getListingRef(property);
  const addr = listingStreetAddress(property);
  if (ref && addr) return `ref ${ref} · ${addr}`;
  if (ref) return `ref ${ref}`;
  return addr || 'Untitled listing';
}

/**
 * Canonical display label for a listing shown in dropdowns, pickers and list
 * views. Always prefixes the (cleaned) title with the reference number when
 * one exists, so users can search / filter on both. e.g.
 *   "ref GGf22677 - 4 Bedroom House, Melodie"
 * Falls back to just the cleaned title when there is no reference.
 *
 * Listing cards prefer address over marketing heading (see buildListingCardLabel).
 *
 * Accepts either a property object or a plain title string (for legacy call
 * sites that only have the title on hand).
 */
export function buildListingLabel(property, opts = {}) {
  if (property && typeof property === 'object') {
    const locationDetails =
      opts.locationDetails ||
      property.locationDetails ||
      (property.details && property.details.locationDetails);
    const addr = listingStreetAddress(property);
    const rawTitle =
      addr ||
      property.propertyTitle ||
      property.title ||
      (property.details && property.details.title) ||
      property.location ||
      property.address ||
      property.suburb ||
      opts.fallbackTitle ||
      'Untitled listing';
    const title = dedupePropertyTitle(rawTitle, locationDetails);
    const ref = getListingRef(property);
    return ref ? `ref ${ref} - ${title}` : title;
  }
  // plain title string
  const title = dedupePropertyTitle(property || opts.fallbackTitle || 'Untitled listing');
  const ref = (opts.ref || '').toString().trim();
  return ref ? `ref ${ref} - ${title}` : title;
}

/**
 * Build a plain-text property brief the AI social-caption endpoint can work
 * from. Listing objects arriving from the dashboard/admin vary a lot in shape,
 * so we read defensively from every field name we've seen and only include
 * what's actually present (the endpoint is told never to invent the rest).
 */
export function buildCaptionContext(listing) {
  if (!listing || typeof listing !== 'object') return '';
  const details = listing.details || {};
  const loc = listing.locationDetails || details.locationDetails || {};
  const lines = [];
  const push = (labelText, value) => {
    if (value === undefined || value === null) return;
    const v = String(value).trim();
    if (v) lines.push(`${labelText}: ${v}`);
  };

  const title = dedupePropertyTitle(
    listing.propertyTitle || listing.title || details.title || listing.propertyName || listing.address || '',
    loc,
  );
  push('Title', title);
  push('Property type', listing.propertyType || details.propertyType);
  push('Listing type', listing.listingType || listing.transactionType || (listing.isRental ? 'To rent' : ''));
  const locality = listing.location
    || [loc.suburb, loc.city, loc.province].filter(Boolean).join(', ');
  push('Location', locality);
  push('Price', listing.priceLabel || listing.displayPrice || listing.price || listing.rentalPrice);
  push('Bedrooms', listing.bedrooms ?? details.bedrooms);
  push('Bathrooms', listing.bathrooms ?? details.bathrooms);
  push('Garages / parking', listing.garages ?? listing.parking ?? details.garages);
  push('Floor size', listing.floorSize || listing.size || details.floorSize);
  push('Erf / land size', listing.erfSize || listing.landSize || details.erfSize);

  const features = listing.features || listing.amenities || details.features;
  if (Array.isArray(features) && features.length) {
    push('Features', features.map((f) => (typeof f === 'string' ? f : f?.name)).filter(Boolean).join(', '));
  }
  push('Marketing heading', listing.marketingHeading || details.marketingHeading);
  const description = listing.description || details.description;
  if (description) push('Description', String(description).replace(/\s+/g, ' ').slice(0, 600));

  return lines.join('\n');
}

/**
 * Lowercased searchable haystack for a listing - reference + title + location.
 * Use in client-side filters so typing either the ref or the name matches.
 */
export function listingSearchText(property) {
  if (!property || typeof property !== 'object') return '';
  return [
    getListingRef(property),
    property.propertyTitle,
    property.title,
    property.location,
    property.address,
    property.suburb,
    property.locationDetails && property.locationDetails.suburb,
    property.locationDetails && property.locationDetails.city,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}
